import {
  CreateServiceAction,
  UpdateServiceAction,
  DeleteServiceAction,
} from "../../../../../store/action";

/**
 * Service management utilities
 */

/** The durations the wizard offers as one tap; anything else is typed in. */
export const DURATION_PRESETS = [15, 30, 45, 60];

/**
 * A duration in minutes, whatever unit it was stored in. Older services (and
 * ones made on the Services page) may say "Hours" or "hours"; the wizard edits
 * minutes only, and saves them back as minutes.
 */
export const toMinutes = (duration, unit) => {
  const value = Number(duration) || 0;
  return /^hours?$/i.test(unit || "") ? value * 60 : value;
};

export const createNewService = () => {
  return {
    name: "",
    duration: 30,
    durationUnit: "Minutes",
    id: Date.now(),
  };
};

/**
 * Saves each service in order, creating new ones and updating existing
 * ones. Throws on the first failure — same as before — but now calls
 * `onItemSaved(originalService, result)` right after each individual save
 * succeeds, so the caller can merge the server-assigned id back into its own
 * state incrementally instead of waiting for the whole loop to finish.
 *
 * This matters on a partial failure: without the per-item callback, items
 * saved before the failure keep their client-side temporary ids forever,
 * and `isDbId` below misclassifies them as new on the next retry —
 * creating duplicates. See resourceHelpers.js's saveResources for the twin
 * of this fix.
 */
export const saveServices = async (services, onItemSaved) => {
  const results = [];

  for (const service of services) {
    const isDbId = typeof service.id === "number" && service.id < 1000000;

    const serviceData = {
      name: service.name.trim(),
      duration: toMinutes(service.duration, service.durationUnit),
      durationUnit: "Minutes",
    };

    try {
      let result;
      if (isDbId) {
        result = await UpdateServiceAction(service.id, serviceData);
      } else {
        result = await CreateServiceAction(serviceData);
      }
      results.push(result);
      onItemSaved?.(service, result);
    } catch (error) {
      console.error(`Error saving service ${service.name}:`, error);
      throw error;
    }
  }

  return results;
};

export const deleteService = async (id) => {
  const isDbId = typeof id === "number" && id < 1000000;

  if (isDbId) {
    await DeleteServiceAction(id);
  }
};
